// src/repositories/supabase/SupabaseCompanyRepository.ts
import type { SupabaseClient } from '@supabase/supabase-js';
import type { ICompanyRepository, DBCompany } from '../types';
import type { Company } from '../../types';
import { requireAuth } from '../../services/supabaseClient';
import { COMPANY_COLS } from '../../constants/queryColumns';

function mapRowToDBCompany(row: Record<string, unknown>): DBCompany {
  return {
    dbId:              row.db_id as number | undefined,   // nullable-safe
    firmId:            row.firm_id as string,
    companyId:         row.company_id as string,
    ruc:               row.ruc as string,
    name:              row.razon_social as string,
    regimen:           row.regimen as 'general' | 'mype' | 'rer' | 'nrus',
    periodo:           row.periodo as string,
    jurisdiction:      (row.jurisdiction as 'PE' | 'US') ?? undefined,
    createdAt:         (row.created_at as string | null) ?? undefined,
    createdBy:         (row.created_by as string | null) ?? undefined,
    contadorWhatsapp:  (row.contador_whatsapp as string | null) ?? undefined,
    contadorNombre:    (row.contador_nombre as string | null) ?? undefined,
    contadorEmail:     (row.contador_email as string | null) ?? undefined,
    tipoActividad:     (row.tipo_actividad as 'comercio' | 'servicios' | 'manufactura' | 'mixto' | null) ?? undefined,
    ingresosAnualesUIT: (row.ingresos_anuales_uit as number | null) ?? undefined,
  };
}

export class SupabaseCompanyRepository implements ICompanyRepository {
  private readonly client: SupabaseClient;
  constructor(client: SupabaseClient) { this.client = client; }

  async getAll(firmId: string): Promise<DBCompany[]> {
    await requireAuth();
    const { data, error } = await this.client
      .from('companies')
      .select(COMPANY_COLS)
      .eq('firm_id', firmId)
      .neq('is_deleted', true);
    if (error) throw new Error(`[getAll] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRowToDBCompany);
  }

  async getByRuc(firmId: string, ruc: string): Promise<DBCompany | undefined> {
    await requireAuth();
    const { data, error } = await this.client
      .from('companies')
      .select(COMPANY_COLS)
      .eq('firm_id', firmId)
      .eq('ruc', ruc)
      .neq('is_deleted', true)
      .limit(1);
    if (error) throw new Error(`[getByRuc] ${error.message}`);
    const rows = data ?? [];
    return rows.length > 0 ? mapRowToDBCompany(rows[0] as unknown as Record<string, unknown>) : undefined;
  }

  async create(company: Company): Promise<void> {
    const userId = await requireAuth();
    const { error } = await this.client
      .from('companies')
      .insert({
        firm_id:      userId,
        company_id:   company.companyId,
        ruc:          company.ruc,
        razon_social: company.name,
        regimen:      company.regimen,
        periodo:      company.periodo,
        moneda:       'PEN',
        ingresos_anuales_uit: company.ingresosAnualesUIT ?? null,
      });
    if (error) {
      if (error.code === '23505') throw new Error('RUC ya registrado en esta firma');
      throw new Error(`[create] ${error.message}`);
    }
  }

  async update(firmId: string, companyId: string, fields: {
    razonSocial?: string;
    regimen?: string;
    domicilioFiscal?: string;
    contadorWhatsapp?: string | null;
    contadorNombre?: string | null;
    contadorEmail?: string | null;
    tipoActividad?: 'comercio' | 'servicios' | 'manufactura' | 'mixto' | null;
    ingresosAnualesUIT?: number | null;
  }): Promise<void> {
    await requireAuth();
    const patch: Record<string, string | number | null> = {};
    if (fields.razonSocial        !== undefined) patch['razon_social']        = fields.razonSocial;
    if (fields.regimen             !== undefined) patch['regimen']             = fields.regimen;
    if (fields.domicilioFiscal    !== undefined) patch['domicilio_fiscal']    = fields.domicilioFiscal || null;
    if (fields.contadorWhatsapp   !== undefined) patch['contador_whatsapp']   = fields.contadorWhatsapp || null;
    if (fields.contadorNombre     !== undefined) patch['contador_nombre']     = fields.contadorNombre || null;
    if (fields.contadorEmail      !== undefined) patch['contador_email']      = fields.contadorEmail || null;
    if (fields.tipoActividad      !== undefined) patch['tipo_actividad']      = fields.tipoActividad || null;
    if (fields.ingresosAnualesUIT !== undefined) patch['ingresos_anuales_uit'] = fields.ingresosAnualesUIT ?? null;
    if (Object.keys(patch).length === 0) return;
    const { error } = await this.client
      .from('companies')
      .update(patch)
      .eq('firm_id', firmId)
      .eq('company_id', companyId);
    if (error) throw new Error(`[update] ${error.message}`);
  }

  async delete(firmId: string, companyId: string): Promise<void> {
    await requireAuth();
    // Soft delete — preserve company record for archived financial data context (SA-2)
    const { error } = await this.client
      .from('companies')
      .update({ is_deleted: true })
      .eq('firm_id', firmId)
      .eq('company_id', companyId);
    if (error) throw new Error(`[delete] ${error.message}`);
  }
}
